import { useEffect, useMemo, useRef, useState } from "react";

import { StopOutlined } from "@ant-design/icons";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { Tooltip as AntdTooltip, Tag, TreeSelect } from "antd";
import styled from "styled-components/macro";

import useBetaFeatures from "hooks/useBetaFeatures";
import useFeatureFlags, { getFieldGroupFlag } from "hooks/useFeatureFlags";

import { IGroupColumn } from "models/columns";

import { getProductLabel } from "components/charts/utils/getProductLabel";

import { EntityKind } from "../../models/entityKind";
import { useTable } from "./context";
import { useColumnChooserSize } from "./hooks";

/**
 * EVA-882: Reconcile a column order array when the set of selected columns changes.
 *
 * Rules:
 * - Keys that existed before and still exist → preserved in their previous order.
 * - Keys that are newly added → appended in the order they appear in `newKeys`.
 * - Keys that were removed → dropped.
 *
 * The entity-name header key (e.g. "Header.EntityName") is always prepended by
 * the chooser's `onPropertiesChanged` caller — it must be excluded from `prevOrder`
 * and `newKeys` before calling this function.
 *
 * Exported for unit testing.
 */
export function reconcileColumnOrder(prevOrder: string[], newKeys: string[]): string[] {
  const newSet = new Set(newKeys);
  const kept = prevOrder.filter((k) => newSet.has(k));
  const keptSet = new Set(kept);
  const added = newKeys.filter((k) => !keptSet.has(k));
  // Saved selections may already hold duplicates.
  return [...new Set([...kept, ...added])];
}

/**
 * Collects every column `property` key nested under a category (its own
 * subgroups plus any product-group subgroups). Used to compute the set of
 * properties that must be pruned from a persisted selection when the
 * category's field-group flag is disabled (EVA-10).
 *
 * EVA-10 review fix: verified this traversal is exhaustive against
 * `IGroupColumn` (models/columns.ts) — `IGroupColumn` nests columns via
 * exactly two paths: `subgroups[].columns[]` and
 * `productGroups[].subgroups[].columns[]`. `ISubgroupColumn.columns` holds
 * `IPropertyColumn[]`, which has no further nesting field, and
 * `IProductColumn` only has `subgroups` (no nested `productGroups`), so
 * there is no third path. This mirrors the same two-path walk used to build
 * the tree/property map in `getColumnPropertyTree.ts` (lines ~264-281).
 */
function getCategoryPropertyKeys(category: IGroupColumn): string[] {
  const keys: string[] = [];
  category.subgroups?.forEach((subgroup) => {
    subgroup.columns?.forEach((column) => keys.push(column.property));
  });
  category.productGroups?.forEach((productGroup) => {
    productGroup.subgroups?.forEach((subgroup) => {
      subgroup.columns?.forEach((column) => keys.push(column.property));
    });
  });
  return keys;
}

/** A property entry to display as disabled (non-selectable) in the column chooser. */
export interface DisabledChooserProperty {
  key: string;
  title: string;
  tooltip: string;
}

export interface ColumnChooserInputModel {
  categories: IGroupColumn[];
  selectedPropertyKeys: string[];
  onPropertiesChanged: (propertyKeys: string[]) => void;
  /**
   * Selected keys the tree does not offer (org-geo jobs not yet allowed). An edit
   * keeps them in the selection instead of reconciling them away.
   */
  retainedPropertyKeys?: ReadonlySet<string>;
  /**
   * When set, an additional disabled (non-selectable) entry is shown in the
   * tree for the given property. Used to inform the user that the group column
   * is suppressed for binned numeric/date properties (REQ-UI-GRP-003 / Issue #9).
   */
  disabledGroupProperty?: DisabledChooserProperty;
}

interface TitleProps {
  props: {
    children: string;
    style: {
      color: string;
    };
  };
}

interface IColumnNode {
  children: IColumnNode[];
  key: string;
  value: string;
  name: string;
  type: "node" | "column";
  memberOf: string[];
  properties: string[];
  title: TitleProps;

  [x: string | number | symbol]: unknown;
}

interface IColumnNodeMap {
  [key: string]: IColumnNode;
}

// Keeps subgroup "Header.Operator" from colliding with the column property of the same name (EVA-533).
const GROUP_NODE_PREFIX = "group:";

export default function DataTableColumnChooser(
  input: ColumnChooserInputModel
): JSX.Element {
  const ref = useRef();
  useColumnChooserSize(ref);
  const [{ entityKind }] = useTable();
  const [searchText, setSearchText] = useState("");
  const [selectedNodes, setSelectedNodes] = useState<IColumnNode[]>([]);
  const [nodeMap, setNodeMap] = useState<IColumnNodeMap>({});
  const [treeData, setTreeData] = useState<IColumnNode[]>([]);
  const [defaultLoaded, setDefaultLoaded] = useState(false);
  const [publishPropertiesChanged, setPublishPropertiesChanged] = useState(false);
  // Mirror selectedPropertyKeys in a ref so the publish effect can read the
  // latest order without adding it to the dependency array (avoids extra emit
  // on every external selectedPropertyKeys change).
  const selectedPropertyKeysRef = useRef<string[]>(input.selectedPropertyKeys ?? []);
  selectedPropertyKeysRef.current = input.selectedPropertyKeys ?? [];

  const { hasFeature } = useBetaFeatures();
  const { hasFeature: hasFeatureFlag } = useFeatureFlags();

  // EVA-10: field-group flags. A category (e.g. "Header", "Forecast") whose
  // "<Category> Fields" flag is disabled is hidden from the chooser entirely.
  const visibleCategories = useMemo(
    () =>
      (input.categories ?? []).filter((category) => {
        const flag = getFieldGroupFlag(category.key);
        return !flag || hasFeatureFlag(flag);
      }),
    [input.categories, hasFeatureFlag]
  );

  // EVA-10: property keys belonging to a now-hidden category. A selection
  // persisted (sessionStorage / saved workspace) from before the category's
  // flag was disabled may still contain these — see the pruning effect below.
  const hiddenPropertyKeys = useMemo(() => {
    const visibleKeys = new Set(visibleCategories.map((category) => category.key));
    const hiddenCategories = (input.categories ?? []).filter(
      (category) => !visibleKeys.has(category.key)
    );
    return new Set(hiddenCategories.flatMap(getCategoryPropertyKeys));
  }, [input.categories, visibleCategories]);

  // Prune any persisted selectedPropertyKeys that fall under a hidden category.
  // One-way: reads input.selectedPropertyKeys and — only when it actually contains
  // a now-hidden key — calls onPropertiesChanged directly, without touching
  // selectedNodes/publishPropertiesChanged, so it can't loop with the publish
  // effect below (which is what feeds selectedNodes back out via that callback).
  //
  // This prune is deliberately destructive (persisted), not display-time-only:
  // field-group flags are long-lived entitlements, not transient UI state, so a
  // hidden key dropped here stays dropped. Re-enabling the flag later does NOT
  // restore the previous selection — the user must re-select those columns. The
  // alternative (filtering only at render time, leaving hiddenPropertyKeys in
  // the persisted selection) was rejected because it would keep requesting
  // columns for a field group the org is no longer licensed for on every
  // data-table load, even though none of them are ever shown.
  useEffect(() => {
    if (hiddenPropertyKeys.size === 0) return;
    const keys = input.selectedPropertyKeys ?? [];
    const hasHiddenKey = keys.some((key) => hiddenPropertyKeys.has(key));
    if (hasHiddenKey) {
      input.onPropertiesChanged(keys.filter((key) => !hiddenPropertyKeys.has(key)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.selectedPropertyKeys, hiddenPropertyKeys]);

  // DataTable passes a new object every render; depend on its fields so the tree isn't rebuilt each time.
  const {
    key: disabledKey,
    title: disabledTitle,
    tooltip: disabledTooltip
  } = input.disabledGroupProperty ?? {};

  useEffect(() => {
    const newTreeData: IColumnNode[] = visibleCategories.map((group) => {
      const properties = [];
      const children = [];
      children.push(
        ...group.subgroups
          // Filter before building so a hidden 3DV subgroup's columns stay out of the Geology group's properties.
          .filter(
            (subgroup) =>
              hasFeature("3DV Columns") ||
              !(group.key === "Geology" && subgroup.key === "3DV")
          )
          .map((subgroup) => {
            const subkey = `${GROUP_NODE_PREFIX}${group.key}.${subgroup.key}`;
            const subproperties = [];
            const columns = [];
            subproperties.push(
              ...subgroup.columns
                .filter(
                  (column) =>
                    column.property !== "Header.EntityName" &&
                    column.property !== "Header.FacilityId"
                )
                .map((column) => column.property)
            );
            properties.push(...subproperties);
            columns.push(
              ...subgroup.columns.reduce((list, column) => {
                const key = column.property;
                // Entity name is always selected, so remove from tree.
                if (key === "Header.EntityName" || key === "Header.FacilityId") {
                  return list;
                }
                list.push({
                  title: (
                    <span
                      key={key}
                      style={{
                        color: group.hexColor
                      }}>
                      {column.title}
                    </span>
                  ),
                  key: key,
                  value: key,
                  name: column.title,
                  type: "column",
                  memberOf: [group.key, subkey],
                  children: [],
                  sortOrder: column.sortOrder
                });
                return list;
              }, [])
            );
            return {
              title: (
                <span
                  key={subkey}
                  style={{
                    color: group.hexColor
                  }}>
                  {subgroup.title}
                </span>
              ),
              key: subkey,
              value: subkey,
              name: subgroup.title,
              type: "node",
              memberOf: [group.key],
              properties: subproperties,
              children: columns,
              sortOrder: subgroup.sortOrder
            };
          })
      );

      children.push(
        ...group.productGroups.map((productGroup) => {
          const prodchildren = [];
          const prodkey = `${GROUP_NODE_PREFIX}${group.key}.${productGroup.productKey}`;
          const prodproperties = [];
          prodchildren.push(
            ...productGroup.subgroups.map((subgroup) => {
              const subkey = `${GROUP_NODE_PREFIX}${group.key}.${productGroup.productKey}.${subgroup.key}`;
              const subproperties = [];
              const columns = [];
              subproperties.push(...subgroup.columns.map((column) => column.property));
              prodproperties.push(...subproperties);
              properties.push(...subproperties);
              columns.push(
                ...subgroup.columns.map((column) => {
                  const key = column.property;
                  return {
                    title: (
                      <span
                        key={key}
                        style={{
                          color: group.hexColor
                        }}>
                        {column.title}
                      </span>
                    ),
                    key: key,
                    value: key,
                    name: column.title,
                    type: "column",
                    memberOf: [group.key, prodkey, subkey],
                    children: [],
                    sortOrder: column.sortOrder
                  };
                })
              );
              return {
                title: (
                  <span
                    key={subkey}
                    style={{
                      color: group.hexColor
                    }}>
                    {subgroup.title}
                  </span>
                ),
                key: subkey,
                value: subkey,
                name: subgroup.title,
                type: "node",
                memberOf: [group.key, prodkey],
                properties: subproperties,
                children: columns,
                sortOrder: subgroup.sortOrder
              };
            })
          );
          return {
            title: (
              <span
                key={prodkey}
                style={{
                  color: group.hexColor
                }}>
                {getProductLabel(productGroup.productDisplayName, entityKind)}
              </span>
            ),
            key: prodkey,
            value: prodkey,
            name: productGroup.productDisplayName,
            type: "node",
            memberOf: [group.key],
            properties: prodproperties,
            children: prodchildren
          };
        })
      );
      return {
        title: (
          <span
            key={group.key}
            style={{
              color: group.hexColor
            }}>
            {group.title}
          </span>
        ),
        key: group.key,
        value: group.key,
        name: group.title,
        type: "node",
        memberOf: [],
        properties: properties,
        children: children,
        sortOrder: group.sortOrder
      };
    });

    // REQ-UI-GRP-003 / Issue #9: When a disabled group property is provided
    // (binned-numeric suppression), append a non-selectable entry so users
    // know the column is unavailable and why — satisfying Nielsen Heuristic #1
    // (Visibility of system status) and Heuristic #9 (Help users recognize,
    // diagnose, and recover from errors).
    if (disabledKey) {
      newTreeData.push({
        title: (
          <AntdTooltip title={disabledTooltip} placement="right">
            <span style={{ color: "#999", fontStyle: "italic" }}>
              {disabledTitle} (Group) — unavailable
            </span>
          </AntdTooltip>
        ) as unknown as TitleProps,
        key: `${disabledKey}__disabled`,
        value: `${disabledKey}__disabled`,
        name: `${disabledTitle} (unavailable)`,
        type: "column" as const,
        memberOf: [],
        properties: [],
        children: [],
        disabled: true,
        sortOrder: -1
      } as IColumnNode);
    }

    setTreeData(newTreeData);
  }, [
    visibleCategories,
    disabledKey,
    disabledTitle,
    disabledTooltip,
    hasFeature,
    entityKind
  ]);

  useEffect(() => {
    if (treeData && treeData.length > 0) {
      if (
        !defaultLoaded &&
        selectedNodes.length === 0 &&
        (!input.selectedPropertyKeys || input.selectedPropertyKeys.length === 0)
      ) {
        // Default the selection to the Header group.
        const defaultNode = treeData[0];
        setSelectedNodes([defaultNode]);
        setDefaultLoaded(true);
        setPublishPropertiesChanged(true);
      }

      const getColumnNodes = (node) => {
        const nodes = [];
        if (node.children) {
          nodes.push(
            ...node.children.flatMap((n) => {
              return getColumnNodes(n);
            })
          );
        }

        if (node.type === "column") {
          nodes.push(node);
        }

        return nodes;
      };
      const newNodeMap: IColumnNodeMap = {};
      treeData.forEach((node) => {
        const columnNodes = getColumnNodes(node);
        columnNodes.forEach((columnNode) => {
          newNodeMap[columnNode.key] = columnNode;
        });
      });
      setNodeMap(newNodeMap);
    }
  }, [treeData]);

  useEffect(() => {
    if (selectedNodes && publishPropertiesChanged) {
      // Collect the raw (unordered) set of newly-selected property keys from the tree.
      const newKeys = selectedNodes.reduce<string[]>((list, n) => {
        if (n.type === "column") {
          list.push(n.key);
        } else if (n.type === "node") {
          list.push(...n.properties);
        }
        return list;
      }, []);

      // EVA-882: Reconcile order — preserve the user's drag-reordered order.
      // prevOrder is selectedPropertyKeys minus the always-prepended entity header.
      const entityHeader =
        entityKind === EntityKind.Well ? "Header.EntityName" : "Header.FacilityId";
      const prevOrder = selectedPropertyKeysRef.current.filter(
        (k) => k !== entityHeader && k !== "group-membership"
      );
      const retainedKeys = prevOrder.filter(
        (k) => input.retainedPropertyKeys?.has(k) && !newKeys.includes(k)
      );
      const properties = reconcileColumnOrder(prevOrder, [...newKeys, ...retainedKeys]);

      // Entity name is always selected, so remove from tree.
      input.onPropertiesChanged &&
        input.onPropertiesChanged(
          entityKind === EntityKind.Well
            ? ["Header.EntityName", ...properties]
            : ["Header.FacilityId", ...properties]
        );
      setPublishPropertiesChanged(false);
    }
  }, [selectedNodes, publishPropertiesChanged]);

  useEffect(() => {
    if (input.selectedPropertyKeys) {
      // REQ-UI-GRP-004: Exclude the system-injected group-membership column from
      // the chooser. Its visibility is controlled exclusively by isGroupMode + dataTableView.
      // OQ-9 resolved (option a): suppress the system column; no duplicate when the user
      // also selects the group property manually — the group-membership column shows the
      // raw group-property value and the user-selected property column would be redundant.
      const chooserPropertyKeys = input.selectedPropertyKeys.filter(
        (key) => key !== "group-membership"
      );
      const newSelectedNodes = [];
      chooserPropertyKeys.forEach((key) => {
        const node = nodeMap[key];
        if (node) {
          newSelectedNodes.push(node);
        }
      });
      // A column toggle comes back from the parent as the same nodes; skip that render.
      setSelectedNodes((prev) =>
        prev.length === newSelectedNodes.length &&
        prev.every((node, i) => node === newSelectedNodes[i])
          ? prev
          : newSelectedNodes
      );
    }
  }, [nodeMap, input.selectedPropertyKeys]);

  function getParentNodes(treeData, selectedNodes) {
    const selectedKeys = new Set(selectedNodes.map((node) => node.key));
    const result = new Set();

    function traverseTree(node) {
      if (!node.children || node.children.length === 0) {
        // Add leaf node if it's selected
        if (selectedKeys.has(node.key)) {
          result.add(node);
          return true;
        }
        return false;
      }

      let allChildrenSelected = true;
      let atLeastOneChildSelected = false;

      for (const child of node.children) {
        const childSelected = traverseTree(child);
        if (childSelected) {
          atLeastOneChildSelected = true;
        } else {
          allChildrenSelected = false;
        }
      }

      if (allChildrenSelected) {
        // If all children are selected, include the parent and remove the children
        result.add(node);
        node.children.forEach((child) => result.delete(child));
      } else if (atLeastOneChildSelected) {
        // If not all siblings are selected, only include selected children
        node.children.forEach((child) => {
          if (selectedKeys.has(child.key)) {
            result.add(child);
          }
        });
      }

      return allChildrenSelected;
    }

    // Start traversal from the root nodes
    treeData.forEach((rootNode) => traverseTree(rootNode));

    return Array.from(result) as IColumnNode[];
  }

  const parentNodes = useMemo(
    () => getParentNodes(treeData, selectedNodes),
    [treeData, selectedNodes]
  );

  if (!input.categories) {
    return null;
  }

  const onColumnSelected = (key, node) => {
    // Deselect any nodes that are a child of the newly selected node.
    const nodes = [...selectedNodes].filter(
      (n) => !n.memberOf || !n.memberOf.includes(key)
    );
    nodes.push(node);
    setSelectedNodes(nodes);
    setPublishPropertiesChanged(true);
  };

  const onColumnDeselected = (key, node) => {
    let modified = false;
    const nodes = [...selectedNodes];
    const index = nodes.map((n) => n.key).indexOf(key);
    if (index >= 0) {
      nodes.splice(index, 1);
      modified = true;
    }

    // Determine if any children need to be deselected
    if (node.type === "node") {
      node.properties.forEach((propertyKey) => {
        const cindex = nodes.map((n) => n.key).indexOf(propertyKey);
        if (cindex >= 0) {
          nodes.splice(cindex, 1);
          modified = true;
        }
      });
    }

    if (modified) {
      setSelectedNodes(nodes);
      setPublishPropertiesChanged(true);
    }
  };

  const handleRemoveNode = (node: IColumnNode) => {
    onColumnDeselected(node.key, node);
  };

  return (
    <Wrapper ref={ref}>
      <FlexContainer>
        <StyledTreeSelect
          allowClear
          placeholder="Please select"
          searchValue={searchText}
          showArrow
          showSearch
          suffixIcon={<KeyboardArrowDownIcon fontSize="large" />}
          switcherIcon={
            <SwitcherIcon role="img">
              <KeyboardArrowDownIcon fontSize="large" />
            </SwitcherIcon>
          }
          listHeight={400}
          showCheckedStrategy={TreeSelect.SHOW_PARENT}
          treeCheckable
          treeData={treeData}
          treeNodeFilterProp="name"
          value={selectedNodes.map((n) => n.key)}
          onClear={() => {
            setSelectedNodes([]);
            setPublishPropertiesChanged(true);
          }}
          onDeselect={onColumnDeselected}
          onSelect={onColumnSelected}
          onSearch={(v) => setSearchText(v)}
          onDropdownVisibleChange={(v) => v || setSearchText("")}
        />
        <AntdTooltip
          color="rgba(255, 255, 255, 0.98)"
          title={<ColumnTags nodes={parentNodes} onRemove={handleRemoveNode} />}
          overlayStyle={{ maxWidth: "300px" }}>
          <SwitcherIcon role="img">
            <MoreVertIcon fontSize="large" />
          </SwitcherIcon>
        </AntdTooltip>
      </FlexContainer>
    </Wrapper>
  );
}

const preventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

interface ColumnTagsProps {
  nodes: IColumnNode[];
  onRemove: (node: IColumnNode) => void;
}

function ColumnTags({ nodes, onRemove }: ColumnTagsProps): JSX.Element {
  if (!nodes || nodes.length === 0) {
    return (
      <Tag
        icon={<StopOutlined />}
        color="red"
        onMouseDown={preventMouseDown}
        style={{
          marginInlineEnd: 4,
          fontSize: 14
        }}>
        No columns selected
      </Tag>
    );
  }

  return (
    <div
      style={{
        maxHeight: "900px",
        maxWidth: "300px",
        overflowY: "auto",
        overflowX: "auto"
      }}>
      {nodes.map((node) => (
        <Tag
          key={node.key}
          closable
          onClose={() => onRemove(node)}
          onMouseDown={preventMouseDown}
          style={{
            marginInlineEnd: 4,
            fontSize: 14,
            color: node.title.props.style.color
          }}>
          {node.name}
        </Tag>
      ))}
    </div>
  );
}

export const Wrapper = styled.div`
  display: grid;
`;

const SwitcherIcon = styled.span`
  transform: translateY(2px);
  color: rgba(var(--color-text-rgb), 0.3);
`;

export const StyledTreeSelect = styled(TreeSelect)`
  flex-grow: 1;
  .ant-select-selector {
    min-width: 400px;
    overflow-y: auto;
    overflow-x: auto;
    max-height: 40px;
  }

  .ant-select-arrow {
    transform: rotate(270deg);
    transition: all 0.2s;
  }

  &.ant-select-open .ant-select-arrow {
    transform: rotate(360deg);
  }

  &.ant-select .ant-select-selection-item,
  .ant-tree-select .ant-select-selection-item {
    max-width: 340px;
  }
`;

export const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;
